import { functionDoc } from '../functionDoc';
import { ROLES, SHOTS } from '../docHelpers';

/** Documentación detallada (plantilla 12 apartados) — Activos fijos. */
export const activosSections = [
  {
    id: "activos-listado",
    name: "Listado de activos",
    type: "list",
    route: "/activos/listado",
    locationPath: "Activos → Listado",
    generalDescription: "Inventario maestro de activos fijos: código, descripción, categoría, área, valor y vida útil. Punto de alta, edición e impresión de etiquetas si está habilitado.",
    prerequisites: [
      "Módulo Activos fijos habilitado.",
      "Usuario administrador (no limitado ni contador)."
    ],
    interfaceElements: [
      {
        name: "Filtros",
        controlType: "Campo",
        required: false,
        editable: true,
        automatic: false,
        description: "Por código, área o categoría.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Tabla de activos",
        controlType: "Tabla",
        required: false,
        editable: true,
        automatic: true,
        description: "Estado y valores resumidos.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Nuevo activo",
        controlType: "Botón",
        required: false,
        editable: true,
        automatic: false,
        description: "Abre formulario de alta.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      }
    ],
    procedure: [
      "Activos → Listado.",
      "Filtre o busque.",
      "Nuevo para alta o Editar en fila.",
      "Guarde cambios."
    ],
    practicalExample: {
      context: "Uso en operación diaria.",
      data: {
        Pantalla: "Listado de activos"
      },
      steps: [
        "Activos → Listado.",
        "Filtre o busque.",
        "Nuevo para alta o Editar en fila.",
        "Guarde cambios."
      ]
    },
    validations: ['Revise campos obligatorios y mensajes de validación en pantalla antes de guardar.'],
    expectedResult: ['El registro queda guardado y aparece en listados o reportes del módulo Activos.'],
    commonErrors: [
      {
        error: "No veo el ítem en el menú lateral",
        cause: "Módulo desactivado o rol sin permiso.",
        solution: "Solicite al administrador habilitar el módulo y revisar el rol del usuario."
      },
      {
        error: "Error al guardar o cargar",
        cause: "Campos obligatorios vacíos, sesión expirada o error de red.",
        solution: "Complete validaciones, recargue la página o vuelva a iniciar sesión."
      }
    ],
    relatedFunctions: [
      "activos-en-uso",
      "activos-depreciacion"
    ],
    observations: [
      ROLES.admin
    ],
    screenshots: [
      SHOTS.listado,
      SHOTS.formularioCompleto
    ],
    roles: ROLES.admin
  },

  {
    id: "activos-en-uso",
    name: "En uso por áreas",
    type: "screen",
    route: "/activos/en-uso",
    locationPath: "Activos → En uso por áreas",
    generalDescription: "Vista de activos asignados por área operativa. Útil para inventario físico y reasignación entre departamentos.",
    prerequisites: [
      "Módulo Activos fijos habilitado.",
      "Usuario administrador (no limitado ni contador)."
    ],
    interfaceElements: [
      {
        name: "Filtro por área",
        controlType: "Select",
        required: false,
        editable: true,
        automatic: false,
        description: "Muestra solo activos de un sitio.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Tabla por área",
        controlType: "Tabla",
        required: false,
        editable: true,
        automatic: true,
        description: "Activos y responsables si aplica.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      }
    ],
    procedure: [
      "Seleccione área.",
      "Revise activos asignados.",
      "Reasigne editando el activo en Listado si corresponde."
    ],
    practicalExample: {
      context: "Uso en operación diaria.",
      data: {
        Pantalla: "En uso por áreas"
      },
      steps: [
        "Seleccione área.",
        "Revise activos asignados.",
        "Reasigne editando el activo en Listado si corresponde."
      ]
    },
    validations: ['Revise campos obligatorios y mensajes de validación en pantalla antes de guardar.'],
    expectedResult: ['El registro queda guardado y aparece en listados o reportes del módulo Activos.'],
    commonErrors: [
      {
        error: "No veo el ítem en el menú lateral",
        cause: "Módulo desactivado o rol sin permiso.",
        solution: "Solicite al administrador habilitar el módulo y revisar el rol del usuario."
      },
      {
        error: "Error al guardar o cargar",
        cause: "Campos obligatorios vacíos, sesión expirada o error de red.",
        solution: "Complete validaciones, recargue la página o vuelva a iniciar sesión."
      }
    ],
    relatedFunctions: [
      "activos-areas",
      "activos-listado"
    ],
    observations: [
      ROLES.admin
    ],
    screenshots: [
      SHOTS.listado,
      SHOTS.formularioCompleto
    ],
    roles: ROLES.admin
  },

  {
    id: "activos-depreciacion",
    name: "Depreciación",
    type: "report",
    route: "/activos/financiero/depreciacion",
    locationPath: "Activos → Depreciación",
    generalDescription: "Cálculo de depreciación por período según vida útil y método configurado. Alimenta contabilidad y reportes financieros del cliente.",
    prerequisites: [
      "Módulo Activos fijos habilitado.",
      "Usuario administrador (no limitado ni contador)."
    ],
    interfaceElements: [
      {
        name: "Período",
        controlType: "Campo",
        required: true,
        editable: true,
        automatic: false,
        description: "Mes o año a depreciar.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Detalle por activo",
        controlType: "Tabla",
        required: false,
        editable: true,
        automatic: true,
        description: "Monto del período y acumulado.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Exportar",
        controlType: "Botón",
        required: false,
        editable: true,
        automatic: false,
        description: "Para ERP o hoja de trabajo.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      }
    ],
    procedure: [
      "Elija período.",
      "Genere o actualice cálculo.",
      "Revise montos por activo.",
      "Exporte para asientos contables."
    ],
    practicalExample: {
      context: "Uso en operación diaria.",
      data: {
        Pantalla: "Depreciación"
      },
      steps: [
        "Elija período.",
        "Genere o actualice cálculo.",
        "Revise montos por activo.",
        "Exporte para asientos contables."
      ]
    },
    validations: ['Revise campos obligatorios y mensajes de validación en pantalla antes de guardar.'],
    expectedResult: ['El registro queda guardado y aparece en listados o reportes del módulo Activos.'],
    commonErrors: [
      {
        error: "No veo el ítem en el menú lateral",
        cause: "Módulo desactivado o rol sin permiso.",
        solution: "Solicite al administrador habilitar el módulo y revisar el rol del usuario."
      },
      {
        error: "Error al guardar o cargar",
        cause: "Campos obligatorios vacíos, sesión expirada o error de red.",
        solution: "Complete validaciones, recargue la página o vuelva a iniciar sesión."
      }
    ],
    relatedFunctions: [
      "activos-listado"
    ],
    observations: [
      ROLES.admin
    ],
    screenshots: [
      SHOTS.listado,
      SHOTS.formularioCompleto
    ],
    roles: ROLES.admin
  },

  {
    id: "activos-clasif-categorias",
    name: "Clasificación — Categorías",
    type: "form",
    route: "/activos/configuracion/clasificacion/categorias",
    locationPath: "Activos → Configuración → Clasificación → Categorías",
    generalDescription: "Nivel superior de clasificación de activos. Define agrupaciones para reportes y políticas de depreciación por tipo.",
    prerequisites: [
      "Módulo Activos fijos habilitado.",
      "Usuario administrador (no limitado ni contador)."
    ],
    interfaceElements: [
      {
        name: "Nombre",
        controlType: "Campo",
        required: true,
        editable: true,
        automatic: false,
        description: "Categoría contable u operativa.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Listado jerárquico",
        controlType: "Tabla",
        required: false,
        editable: true,
        automatic: false,
        description: "Enlace a subcategorías.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      }
    ],
    procedure: [
      "Configure categorías antes del alta masiva.",
      "Cree subcategorías y familias en niveles inferiores.",
      "Asigne cada activo en el listado."
    ],
    practicalExample: {
      context: "Uso en operación diaria.",
      data: {
        Pantalla: "Clasificación — Categorías"
      },
      steps: [
        "Configure categorías antes del alta masiva.",
        "Cree subcategorías y familias en niveles inferiores.",
        "Asigne cada activo en el listado."
      ]
    },
    validations: ['Revise campos obligatorios y mensajes de validación en pantalla antes de guardar.'],
    expectedResult: ['El registro queda guardado y aparece en listados o reportes del módulo Activos.'],
    commonErrors: [
      {
        error: "No veo el ítem en el menú lateral",
        cause: "Módulo desactivado o rol sin permiso.",
        solution: "Solicite al administrador habilitar el módulo y revisar el rol del usuario."
      },
      {
        error: "Error al guardar o cargar",
        cause: "Campos obligatorios vacíos, sesión expirada o error de red.",
        solution: "Complete validaciones, recargue la página o vuelva a iniciar sesión."
      }
    ],
    relatedFunctions: [
      "activos-clasif-subcategorias",
      "activos-listado"
    ],
    observations: [
      ROLES.admin
    ],
    screenshots: [
      SHOTS.listado,
      SHOTS.formularioCompleto
    ],
    roles: ROLES.admin
  },

  {
    id: "activos-clasif-subcategorias",
    name: "Clasificación — Subcategorías",
    type: "form",
    route: "/activos/configuracion/clasificacion/subcategorias",
    locationPath: "Activos → Configuración → Clasificación → Subcategorías",
    generalDescription: "Segundo nivel bajo una categoría. Refina la clasificación para inventario y depreciación.",
    prerequisites: [
      "Módulo Activos fijos habilitado.",
      "Usuario administrador (no limitado ni contador)."
    ],
    interfaceElements: [
      {
        name: "Categoría padre",
        controlType: "Select",
        required: true,
        editable: true,
        automatic: false,
        description: "Obligatoria.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Nombre subcategoría",
        controlType: "Campo",
        required: true,
        editable: true,
        automatic: false,
        description: "Etiqueta del nivel.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      }
    ],
    procedure: [
      "Seleccione categoría.",
      "Cree subcategorías necesarias.",
      "Use en familias y en altas de activos."
    ],
    practicalExample: {
      context: "Uso en operación diaria.",
      data: {
        Pantalla: "Clasificación — Subcategorías"
      },
      steps: [
        "Seleccione categoría.",
        "Cree subcategorías necesarias.",
        "Use en familias y en altas de activos."
      ]
    },
    validations: ['Revise campos obligatorios y mensajes de validación en pantalla antes de guardar.'],
    expectedResult: ['El registro queda guardado y aparece en listados o reportes del módulo Activos.'],
    commonErrors: [
      {
        error: "No veo el ítem en el menú lateral",
        cause: "Módulo desactivado o rol sin permiso.",
        solution: "Solicite al administrador habilitar el módulo y revisar el rol del usuario."
      },
      {
        error: "Error al guardar o cargar",
        cause: "Campos obligatorios vacíos, sesión expirada o error de red.",
        solution: "Complete validaciones, recargue la página o vuelva a iniciar sesión."
      }
    ],
    relatedFunctions: [
      "activos-clasif-categorias",
      "activos-clasif-familias"
    ],
    observations: [
      ROLES.admin
    ],
    screenshots: [
      SHOTS.listado,
      SHOTS.formularioCompleto
    ],
    roles: ROLES.admin
  },

  {
    id: "activos-clasif-familias",
    name: "Clasificación — Familias",
    type: "form",
    route: "/activos/configuracion/clasificacion/familias",
    locationPath: "Activos → Configuración → Clasificación → Familias",
    generalDescription: "Nivel más detallado de clasificación (familia de activo). Facilita reportes homogéneos (mobiliario, TI, vehículos internos, etc.).",
    prerequisites: [
      "Módulo Activos fijos habilitado.",
      "Usuario administrador (no limitado ni contador)."
    ],
    interfaceElements: [
      {
        name: "Subcategoría padre",
        controlType: "Select",
        required: true,
        editable: true,
        automatic: false,
        description: "Jerarquía completa.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Nombre familia",
        controlType: "Campo",
        required: true,
        editable: true,
        automatic: false,
        description: "Identificador fino.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      }
    ],
    procedure: [
      "Mantenga coherencia con categoría y subcategoría.",
      "Asigne familia al crear activos."
    ],
    practicalExample: {
      context: "Uso en operación diaria.",
      data: {
        Pantalla: "Clasificación — Familias"
      },
      steps: [
        "Mantenga coherencia con categoría y subcategoría.",
        "Asigne familia al crear activos."
      ]
    },
    validations: ['Revise campos obligatorios y mensajes de validación en pantalla antes de guardar.'],
    expectedResult: ['El registro queda guardado y aparece en listados o reportes del módulo Activos.'],
    commonErrors: [
      {
        error: "No veo el ítem en el menú lateral",
        cause: "Módulo desactivado o rol sin permiso.",
        solution: "Solicite al administrador habilitar el módulo y revisar el rol del usuario."
      },
      {
        error: "Error al guardar o cargar",
        cause: "Campos obligatorios vacíos, sesión expirada o error de red.",
        solution: "Complete validaciones, recargue la página o vuelva a iniciar sesión."
      }
    ],
    relatedFunctions: [
      "activos-clasif-jerarquia"
    ],
    observations: [
      ROLES.admin
    ],
    screenshots: [
      SHOTS.listado,
      SHOTS.formularioCompleto
    ],
    roles: ROLES.admin
  },

  {
    id: "activos-clasif-jerarquia",
    name: "Clasificación — Vista jerárquica",
    type: "screen",
    route: "/activos/configuracion/clasificacion/jerarquia",
    locationPath: "Activos → Configuración → Clasificación → Vista jerárquica",
    generalDescription: "Árbol categoría → subcategoría → familia para revisar la estructura completa sin navegar pantalla por pantalla.",
    prerequisites: [
      "Módulo Activos fijos habilitado.",
      "Usuario administrador (no limitado ni contador)."
    ],
    interfaceElements: [
      {
        name: "Árbol de clasificación",
        controlType: "Árbol",
        required: false,
        editable: true,
        automatic: true,
        description: "Nodos expandibles.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Acceso rápido a editar",
        controlType: "Enlace",
        required: false,
        editable: true,
        automatic: false,
        description: "Desde nodo seleccionado.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      }
    ],
    procedure: [
      "Revise que no existan ramas huérfanas.",
      "Corrija en el nivel correspondiente si falta un eslabón."
    ],
    practicalExample: {
      context: "Uso en operación diaria.",
      data: {
        Pantalla: "Clasificación — Vista jerárquica"
      },
      steps: [
        "Revise que no existan ramas huérfanas.",
        "Corrija en el nivel correspondiente si falta un eslabón."
      ]
    },
    validations: ['Revise campos obligatorios y mensajes de validación en pantalla antes de guardar.'],
    expectedResult: ['El registro queda guardado y aparece en listados o reportes del módulo Activos.'],
    commonErrors: [
      {
        error: "No veo el ítem en el menú lateral",
        cause: "Módulo desactivado o rol sin permiso.",
        solution: "Solicite al administrador habilitar el módulo y revisar el rol del usuario."
      },
      {
        error: "Error al guardar o cargar",
        cause: "Campos obligatorios vacíos, sesión expirada o error de red.",
        solution: "Complete validaciones, recargue la página o vuelva a iniciar sesión."
      }
    ],
    relatedFunctions: [
      "activos-clasif-categorias"
    ],
    observations: [
      ROLES.admin
    ],
    screenshots: [
      SHOTS.listado,
      SHOTS.formularioCompleto
    ],
    roles: ROLES.admin
  },

  {
    id: "activos-areas",
    name: "Áreas",
    type: "form",
    route: "/activos/configuracion/areas",
    locationPath: "Activos → Configuración → Áreas",
    generalDescription: "Catálogo de áreas o ubicaciones físicas donde se asignan activos. Usado en «En uso por áreas» y en el alta del activo.",
    prerequisites: [
      "Módulo Activos fijos habilitado.",
      "Usuario administrador (no limitado ni contador)."
    ],
    interfaceElements: [
      {
        name: "Nombre del área",
        controlType: "Campo",
        required: true,
        editable: true,
        automatic: false,
        description: "Planta, oficina, campamento, etc.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Código opcional",
        controlType: "Campo",
        required: false,
        editable: true,
        automatic: false,
        description: "Para integraciones.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      }
    ],
    procedure: [
      "Cree áreas antes de asignar activos.",
      "Mantenga nombres alineados con organigrama.",
      "Reasigne activos al cambiar de área."
    ],
    practicalExample: {
      context: "Uso en operación diaria.",
      data: {
        Pantalla: "Áreas"
      },
      steps: [
        "Cree áreas antes de asignar activos.",
        "Mantenga nombres alineados con organigrama.",
        "Reasigne activos al cambiar de área."
      ]
    },
    validations: ['Revise campos obligatorios y mensajes de validación en pantalla antes de guardar.'],
    expectedResult: ['El registro queda guardado y aparece en listados o reportes del módulo Activos.'],
    commonErrors: [
      {
        error: "No veo el ítem en el menú lateral",
        cause: "Módulo desactivado o rol sin permiso.",
        solution: "Solicite al administrador habilitar el módulo y revisar el rol del usuario."
      },
      {
        error: "Error al guardar o cargar",
        cause: "Campos obligatorios vacíos, sesión expirada o error de red.",
        solution: "Complete validaciones, recargue la página o vuelva a iniciar sesión."
      }
    ],
    relatedFunctions: [
      "activos-en-uso",
      "activos-listado"
    ],
    observations: [
      ROLES.admin
    ],
    screenshots: [
      SHOTS.listado,
      SHOTS.formularioCompleto
    ],
    roles: ROLES.admin
  }
].map((p) => functionDoc(p));
